import CONFIG from './config-backend.js';
import { fetchAuth } from './fetchAuth.js';
import { showError, showWarning, showSuccess } from './notify.js';

const BASE = `${CONFIG.BACKEND_URL}/sender`;

// datetime-local gives YYYY-MM-DDTHH:MM — append :00 for the API
const toApiDate = val => (val && val.length === 16 ? `${val}:00` : val);

// API returns "YYYY-MM-DD HH:MM:SS" — convert back to datetime-local format
const fromApiDate = val => val ? val.replace(' ', 'T').slice(0, 16) : '';

// ── DOM refs ───────────────────────────────────────────────────────────────────
const form        = document.getElementById('resenderForm');
const previewBtn  = document.getElementById('previewBtn');
const sendBtn     = document.getElementById('sendBtn');
const stopBtn     = document.getElementById('stopBtn');
const previewCard = document.getElementById('previewCard');

let pollTimer = null;
let previewCount = null;

// ── helpers ────────────────────────────────────────────────────────────────────
function getFormValues() {
  const from        = toApiDate(document.getElementById('from').value);
  const to          = toApiDate(document.getElementById('to').value);
  const crawlerId   = document.getElementById('crawlerId').value.trim();
  const crawlerType = document.getElementById('crawlerType').value.trim();
  const body = { queue: document.getElementById('queue').value };
  if (from)        body.from         = from;
  if (to)          body.to           = to;
  if (crawlerId)   body.crawler_id   = crawlerId;
  if (crawlerType) body.crawler_type = crawlerType;
  console.log('[sender] body:', JSON.stringify(body, null, 2));
  return body;
}

function setPreviewCard(data) {
  document.getElementById('prevQueue').textContent   = data.queue;
  document.getElementById('prevRange').textContent   =
    data.from && data.to ? `${data.from} → ${data.to}` : '(full table range)';
  document.getElementById('prevType').textContent    = data.crawler_type ?? 'all';
  document.getElementById('prevCrawler').textContent = data.crawler_id ?? 'all';
  previewCount = data.approximate_count ?? 0;
  document.getElementById('prevCount').textContent   = previewCount.toLocaleString();
  previewCard.classList.remove('d-none');
  sendBtn.classList.remove('d-none');

  // Lock the resolved date range into the form so Send uses the exact same bounds
  if (data.from) document.getElementById('from').value = fromApiDate(data.from);
  if (data.to)   document.getElementById('to').value   = fromApiDate(data.to);
}

function updateStatusUI(data) {
  document.getElementById('statusEmpty').classList.add('d-none');
  document.getElementById('statusContent').classList.remove('d-none');

  const badge = document.getElementById('statusBadge');
  if (data.running) {
    badge.className = 'badge bg-warning text-dark me-2';
    badge.textContent = 'Running';
    document.getElementById('progressBar').classList.remove('d-none');
    document.getElementById('statusSpinner').classList.remove('d-none');
    stopBtn.classList.remove('d-none');
  } else {
    badge.className = `badge me-2 ${data.errors > 0 ? 'bg-warning text-dark' : 'bg-success'}`;
    badge.textContent = 'Done';
    document.getElementById('progressBar').classList.add('d-none');
    document.getElementById('statusSpinner').classList.add('d-none');
    stopBtn.classList.add('d-none');
    stopBtn.disabled = false;
    stopBtn.textContent = 'Stop';
  }

  document.getElementById('statusQueue').textContent     = data.queue ?? '—';
  document.getElementById('statusType').textContent      = data.crawler_type ?? 'all';
  document.getElementById('statusCrawler').textContent   = data.crawler_id ?? 'all';
  document.getElementById('statusRange').textContent     =
    data.from && data.to ? `${data.from} → ${data.to}` : '—';
  document.getElementById('statusProcessed').textContent = (data.processed ?? 0).toLocaleString();
  document.getElementById('statusExpected').textContent  = previewCount !== null ? previewCount.toLocaleString() : '—';
  document.getElementById('statusErrors').textContent    = (data.errors ?? 0).toLocaleString();
}

function startPolling() {
  document.getElementById('statusSpinner').classList.remove('d-none');
  document.getElementById('progressBar').classList.remove('d-none');

  const poll = async () => {
    try {
      const res = await fetchAuth(`${BASE}/status`, { method: 'GET' });
      if (!res.ok) return;
      const data = await res.json();
      updateStatusUI(data);
      if (!data.running) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    } catch {
      // silently ignore transient errors
    }
  };

  poll();
  pollTimer = setInterval(poll, 3000);
}

// ── event handlers ─────────────────────────────────────────────────────────────
form.addEventListener('submit', async e => {
  e.preventDefault();
  previewBtn.disabled = true;
  previewBtn.textContent = 'Loading…';
  previewCard.classList.add('d-none');
  sendBtn.classList.add('d-none');

  try {
    const body = getFormValues();
    const res = await fetchAuth(`${BASE}/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.text();
      showError(`Preview failed (${res.status}): ${err}`);
      return;
    }

    const data = await res.json();
    setPreviewCard(data);
  } catch (err) {
    if (err.message !== 'Unauthorized') showError(`Could not reach the server: ${err.message}`);
  } finally {
    previewBtn.disabled = false;
    previewBtn.textContent = 'Preview';
  }
});

sendBtn.addEventListener('click', async () => {
  sendBtn.disabled = true;
  sendBtn.textContent = 'Sending…';

  try {
    const body = getFormValues();
    const res = await fetchAuth(`${BASE}/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (res.status === 409) {
      showWarning('A resend job is already running. Check the status panel.');
      startPolling();
      return;
    }

    if (!res.ok) {
      const err = await res.text();
      showError(`Send failed (${res.status}): ${err}`);
      return;
    }

    showSuccess('Resend job started.');

    previewCard.classList.add('d-none');
    sendBtn.classList.add('d-none');
    startPolling();
  } catch (err) {
    if (err.message !== 'Unauthorized') showError(`Could not reach the server: ${err.message}`);
  } finally {
    sendBtn.disabled = false;
    sendBtn.textContent = 'Confirm & Send';
  }
});

stopBtn.addEventListener('click', async () => {
  stopBtn.disabled = true;
  stopBtn.textContent = 'Stopping…';
  try {
    const res = await fetchAuth(`${BASE}/stop`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.text();
      showError(`Stop failed (${res.status}): ${err}`);
      stopBtn.disabled = false;
      stopBtn.textContent = 'Stop';
    } else {
      showWarning('Stop requested — job will halt after the current batch.');
    }
  } catch (err) {
    if (err.message !== 'Unauthorized') showError(`Could not reach the server: ${err.message}`);
  }
});

// ── init ───────────────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', async () => {
  // Populate crawler select
  try {
    const res = await fetchAuth(`${CONFIG.BACKEND_URL}/crawler`, { method: 'GET' });
    if (res.ok) {
      const crawlers = await res.json();
      const select = document.getElementById('crawlerId');
      for (const c of crawlers) {
        const opt = document.createElement('option');
        opt.value = c.id;
        opt.textContent = c.name ?? c.id;
        select.append(opt);
      }
    }
  } catch {
    // leave select with only the "All crawlers" option
  }

  // Only show status on load if a job is actively running
  try {
    const res = await fetchAuth(`${BASE}/status`, { method: 'GET' });
    if (!res.ok) return;
    const data = await res.json();
    if (data.running) {
      updateStatusUI(data);
      startPolling();
    }
  } catch {
    // resender not reachable — leave "No job" message
  }
});
